import { Box, DownloadMenu, Spinner } from '@ouster/webviz-react-components';

import { Popover } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { FileInfo, OutputFileInfo } from '../../types';
import {
  BASE_ORG_API,
  BASE_PUBLISHED_RECORDING_API,
} from '../../utils/constants';
import { fetchWithAuth } from '../../utils/requests';

type RecordingDownloadPopoverProps = {
  anchorEl: HTMLElement | null;
  closeDownloadMenu: () => void;
  downloadRecordingId: string;
  orgId?: number;
  isPublic?: boolean;
  recordingTitle?: string;
  isSlamProcessing?: boolean;
};

export default function RecordingDownloadPopover({
  anchorEl,
  closeDownloadMenu,
  downloadRecordingId,
  orgId,
  isPublic = false,
  recordingTitle,
  isSlamProcessing = false,
}: RecordingDownloadPopoverProps) {
  const [isLoadingDownloadInfo, setIsLoadingDownloadInfo] = useState(false);
  const { loadSourceFileInfo, loadOutputFileInfo } = useStoreActions(
    (actions) => actions.drive
  );
  const { showToast } = useChakraHook();
  const {
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((storeState) => storeState.drive);

  const open = Boolean(anchorEl);

  useEffect(() => {
    const loadDownloadInfo = async () => {
      try {
        setIsLoadingDownloadInfo(true);
        await loadOutputFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });

        await loadSourceFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });
      } catch (error) {
        // console.error('Error loading download info:', error);
        showToast({
          title: 'Failed to download recording',
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
        closeDownloadMenu();
      } finally {
        setIsLoadingDownloadInfo(false);
      }
    };

    if (orgId && downloadRecordingId) loadDownloadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloadRecordingId,
    isPublic,
    loadOutputFileInfo,
    loadSourceFileInfo,
    orgId,
    showToast,
  ]);

  const id = open ? 'download-popover' : undefined;
  const getFileDownloadUrl = useCallback(
    async (
      driveUuid: string,
      file: FileInfo | OutputFileInfo,
      type: string,
      downloadFilename?: string
    ) => {
      const downloadPath = isPublic
        ? `${BASE_PUBLISHED_RECORDING_API}/${driveUuid}/${type}/files/${file.id}/signed_url`
        : `${BASE_ORG_API}/${orgId}/recordings/${driveUuid}/${type}/files/${file.id}/signed_url`;

      const response = await fetchWithAuth(downloadPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadFilename: file.filename ?? downloadFilename,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Something went wrong when creating signed URL for file ${file.id}`
        );
      }

      return response.json() as Promise<{ url: string }>;
    },
    [isPublic, orgId]
  );

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={closeDownloadMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            overflow: 'hidden',
          },
        },
      }}
      data-testid="recording-download-popover-root"
    >
      <Box p="0" display="flex" gap="12px" alignItems="flex-start">
        {isLoadingDownloadInfo && (
          <Box
            p="16px"
            minH="56px"
            minW="56px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            bg="background-surface"
            outline="1px solid var(--ouster-colors-border-divider)"
            data-testid="recording-download-popover-loading"
          >
            <Spinner data-testid="recording-download-popover-loading-spinner" />
          </Box>
        )}
        {!isLoadingDownloadInfo && (
          <DownloadMenu
            driveUuid={downloadRecordingId}
            revealed
            sourceDataForDownload={sourceDataForDownload}
            sourceConfigForDownload={sourceConfigForDownload}
            downloadableOutputFiles={
              outputForDownload.length > 0
                ? outputForDownload
                : outputForDownloadNonSlam
            }
            onClickedOutside={closeDownloadMenu}
            getFileDownloadUrl={getFileDownloadUrl}
            recordingTitle={recordingTitle}
            popupProps={{
              style: {
                position: 'static',
                top: 'auto',
                right: 'auto',
                zIndex: 'auto',
              },
              'data-testid': 'recording-download-popover-menu',
            }}
            isSlamProcessing={isSlamProcessing}
          />
        )}
      </Box>
    </Popover>
  );
}
